import _ from "lodash";
import React, {useContext, useState} from "react";

import ProductPreview from "./ProductPreview";
import PriceView from "../common/PriceView";
import DeviceChoices from "./DeviceChoices";
import PromoCode from "../common/PromoCode";
import TotalPrice from "../common/TotalPrice";
import ReferralCode from "../common/ReferralCode";
import { buyNowContext, shippingMethods } from "../BuyNow";
import RecurringPlanPrice from "../common/RecurringPlanPrice";
import { formattedNextChargeDate } from "../../utils/orderHelper";
import {DEVICE_TYPES, PRODUCT_CATEGORIES} from "../../../../constants";
import PlanChoices from "./PlanChoices";
import { GuaranteesIcons } from "../common/GuaranteesIcons";
import {AdminAccessQuantity} from "../common/AdminAccessQuantity";

/**
 * This product selection component is specific to parents using the ClearStep program for their child.
 */
const ClearStepParentProductSelection = () => {
  const {
    promoCode,
    PURCHASE_STAGES,
    selectedPlanId,
    setPurchaseStage,
    shippingStageState,
    setShippingStageState,
    scaleBpBundleProducts,
    adminAccessProducts,
    scaleProducts,
    planProducts,
    removeLineItems,
    setLineItemsByCategory,
    lineItems,
    appInfo
  } = useContext(buyNowContext);

  const deviceLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const adminAccessLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.ADMIN_ACCESS);
  const selectedDevice = deviceLineItem ? deviceLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;
  const isSelectedDeviceBundle = selectedDevice ? selectedDevice.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE : false;

  const businessName = appInfo.name;
  const isAdolescentBPDevice = selectedDevice?.name.includes("Adolescent");

  const [previewedDevice, setPreviewedDevice] = useState();
  const [alreadyHasProducts, setAlreadyHasProducts] = useState(false);
  const [quantity, setQuantity] = useState(planLineItem ? planLineItem.quantity : 1);
  const isActiveDeviceChoice = !alreadyHasProducts;
  const isActiveMemberShipSection = selectedDevice && !alreadyHasProducts;
  const isActiveCheckoutSection = (adminAccessLineItem|| selectedDevice || selectedPlan) &&
                                  (alreadyHasProducts || selectedDevice) &&
                                  (alreadyHasProducts || selectedPlan);

  const onAlreadyHasDevice = event => {
    setAlreadyHasProducts(event.target.checked);

    // When this option is either checked or unchecked, let's clear the selected products and reset the promo code.
    removeLineItems([deviceLineItem, planLineItem]);
  };

  const totalPriceDetailsContent = () => {
    if (!isActiveMemberShipSection) {
      return "Please select a MyClearStep scale and a plan";
    }
    else if (!isActiveCheckoutSection) {
      return "Please select a plan";
    }
    else if (selectedPlan?.recurringPlan) {
      return (
        <span>
          {`Your next payment will start on ${formattedNextChargeDate(selectedPlan, promoCode)} at $`}
          <span>
            {<RecurringPlanPrice />}
          </span>
          {"/month"}
        </span>
      );
    }

    return null;
  };

  const proceedToShipping = () => {
    setPurchaseStage(PURCHASE_STAGES.SHIPPING);
  };

  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <h2 id="buy-now-header" className="col-lg-12 text-lg-center d-lg-block">
          MyClearStep numberless scale with portal access
        </h2>
      </div>
      <GuaranteesIcons/>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-lg-5 offset-lg-1">
            <ProductPreview product={adminAccessProducts[0]} className="py-lg-5" />
          </div>

          <section className="col-lg-5 mt-4">
            <section>
              <div className="my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <h4 className={"text-dark header-txt"}>
                  MyClearStep portal access
                </h4>

                <span className="description-txt text-muted">
                  {`A one-time registration fee grants lifetime access to the ${businessName} admin portal.`}
                </span>
              </div>

              <AdminAccessQuantity />
            </section>
          </section>
        </div>

        <div className="row">
          <div className="col-12 col-lg-5 offset-lg-1">
            <ProductPreview product={previewedDevice || selectedDevice || scaleBpBundleProducts[0]}
                            className="py-lg-5"/>
          </div>

          <section className="col-lg-5 mt-4" style={{ display: "none" }}>
            <section>
              <h4 className={"offset-1 offset-lg-0 text-dark header-txt"}>
                {`Going truly numberless. Your ${businessName} purchase includes:`}
              </h4>

              <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <ul>
                  <li className='d-flex'>
                    <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                    <p className="pb-1 description-txt">
                      {`${businessName} ${selectedDevice?.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE ? `numberless scale and${isAdolescentBPDevice ? " adolescent" : ""} blood pressure monitor` : "numberless scale"}`}
                    </p>
                  </li>
                  <li className='d-flex'>
                    <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                    <p className="pb-1 description-txt">
                      {"Medical-grade, FDA-approved"}
                    </p>
                  </li>
                  {isAdolescentBPDevice &&
                    <li className='d-flex'>
                      <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                      <p className="pb-1 description-txt">
                        {"Designed for Adolescents (ages 8-19)"}
                      </p>
                    </li>
                  }
                  <li className='d-flex'>
                    <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                    <p className="pb-1 description-txt">
                      {`Access to ${businessName} app (available in iOS and Android)`}
                    </p>
                  </li>
                  <li className='d-flex'>
                    <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                    <p className="pb-1 description-txt">
                      {`Connection with your team so they can track progress securely through the ${businessName} admin portal`}
                    </p>
                  </li>
                </ul>
              </div>
            </section>
          </section>

            <section className="col-lg-5 mt-4">
              <section>
                <div className="my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                  <h4 className={"text-dark header-txt"}>
                    MyClearStep products
                  </h4>

                  <div>
                  <span className="description-txt text-muted">
                    Feel secure when stepping on the scale without the anxiety of seeing numbers or having to rely on others.
                  </span>
                  </div>

                  <ul className="mt-2 mb-2">
                    <li className='d-flex'>
                      <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                      <p className="pb-1 description-txt">
                        {`${businessName} ${selectedDevice?.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE ? `numberless scale and${isAdolescentBPDevice ? " adolescent" : ""} blood pressure monitor` : "numberless scale"}`}
                      </p>
                    </li>
                    <li className='d-flex'>
                      <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                      <p className="pb-1 description-txt">
                        {"Medical-grade, FDA-approved"}
                      </p>
                    </li>
                    {isAdolescentBPDevice &&
                      <li className='d-flex'>
                        <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                        <p className="pb-1 description-txt">
                          {"Designed for Adolescents (ages 8-19)"}
                        </p>
                      </li>
                    }
                  </ul>

                  {
                    isSelectedDeviceBundle
                      ? <span className="description-txt text-muted">* Scale color may vary between black and oak</span>
                      : null
                  }
                </div>

                <div className={"disabled-overlay " + (isActiveDeviceChoice ? 'd-none' : 'd-block')}/>

                <DeviceChoices
                  selectedDevice={selectedDevice}
                  devices={scaleBpBundleProducts.concat(scaleProducts)}
                  onHovered={device => setPreviewedDevice(device)}
                  onDeviceSelected={device => {
                    const lineItemUpdates = [];

                    // If a scale doesn't have shipping options available, make sure our state reflects 'standard' shipping.
                    if (!device.shippingOptionsAvailable)
                      setShippingStageState({...shippingStageState, shippingMethod: shippingMethods.standard});

                    lineItemUpdates.push({product: device, quantity});

                    // Upon selecting a device, let's select the default plan.
                    // If a plan is selected from elsewhere, we'll use that.
                    if (!selectedPlan) {
                      if (selectedPlanId) {
                        const indexOfSelectedPlan = planProducts.findIndex((plan) => plan.id === parseInt(selectedPlanId));
                        lineItemUpdates.push({product: planProducts[indexOfSelectedPlan], quantity});
                      } else
                        lineItemUpdates.push({product: planProducts.find(p => p.default), quantity});
                    }

                    setLineItemsByCategory(lineItemUpdates);
                  }}
                />
                <div
                  className="form-check d-lg-flex align-items-lg-center col-10 offset-1 px-4 col-lg-12 offset-lg-0 m-2"
                  style={{zIndex: 11}}>
                  <input
                    type="checkbox" className="form-check-input me-2" id="checkboxDevices"
                    defaultChecked={alreadyHasProducts}
                    name="checkboxDevices" onClick={event => onAlreadyHasDevice(event)}
                  />
                  <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="checkboxDevices">
                    I don't want to purchase the MyClearStep products
                  </label>
                </div>
              </section>
            </section>
        </div>

        <div className="row row mt-4 mt-md-0">
          <div className="col-12 col-lg-5 offset-lg-1">

          </div>

          <section className="col-lg-5">
            <div>
              <div className={"disabled-overlay " + (isActiveMemberShipSection ? 'd-none' : 'd-block')}/>

              <div className="col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <h4 className={"text-dark header-txt"}>
                  Monthly Plan
                </h4>

                <span className="description-txt text-muted">
                  A monthly plan connects you to your treatment team through our 100% HIPAA-compliant platform with strong encryption.
                </span>

                <section className="my-1">
                  <PlanChoices
                    selectedPlan={selectedPlan}
                    plans={planProducts}
                    onPlanSelected={plan => {
                      setLineItemsByCategory([{product: plan, quantity}]);
                    }}
                  />

                  {!_.isEmpty(selectedPlan) && selectedPlan.recurringPlan &&
                    <div>
                      <span className="description-txt text-muted">
                        {"Starting today, you will automatically be charged $"}
                        <span className="description-txt text-muted">{<RecurringPlanPrice/>}</span>
                        {"/month. You can cancel anytime before your next billing cycle."}
                      </span>
                    </div>
                  }
                </section>
              </div>

              <br/>
            </div>

            <div className="my-5 text-center">
              <PromoCode/>
              <ReferralCode/>
              <PriceView hideDecimalIfWhole={true} includeShippingCost={false} className="text-center"/>
              <div className={"disabled-overlay pay-btn-section " + (isActiveCheckoutSection ? 'd-none' : 'd-block')}>
              </div>

              <p className="d-block pb-0 checkout-sub-txt">Subtotal</p>
              <h1 className="d-block py-2 display-4 fw-bold total-price-txt">${<TotalPrice hideDecimalIfWhole={true}
                                                                                           includeShippingCost={false}/>}</h1>
              <p className="d-block checkout-sub-txt">
                {totalPriceDetailsContent()}
              </p>

              <div className="text-center py-3">
                <a className="teal button-link next-button-txt next-step-button" onClick={() => { proceedToShipping(); }}>
                  Proceed to checkout
                </a>
              </div>

              <div className="mt-3 text-center">
                <p className="text-muted font-italic most-popular">
                  Sales tax and shipping will be calculated at checkout
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ClearStepParentProductSelection;
